import React from 'react';
import { styled } from '@linaria/react';

import useInterval from '@/hooks/use-interval';
import { normalize } from '@/utils';

import Tooltip from '@/components/Tooltip';
import DotProgressBar from '@/components/DotProgressBar';
import SkipBackIcon from '@/components/AnimatedIcons/SkipBackIcon';

import ActionButton from './ActionButton';

const RESET_AFTER = 1000;

interface Props {
  accentColor?: string;
  handleReset: () => void;
}

const ResetButton = ({
  accentColor = 'var(--color-primary)',
  handleReset,
}: Props) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const [isRunning, setIsRunning] = React.useState(false);
  const [progress, setProgress] = React.useState<number | null>(null);

  const mouseDownAt = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!isRunning) {
      setProgress(null);
    }
  }, [isRunning]);

  function handleMouseDown() {
    mouseDownAt.current = Date.now();
    setIsRunning(true);
    setShowTooltip(false);
  }

  function handleMouseUp() {
    if (!mouseDownAt.current) {
      return;
    }

    setIsRunning(false);

    const delta = Date.now() - mouseDownAt.current;
    if (delta < 400) {
      setShowTooltip(true);
    }
  }

  useInterval(
    () => {
      if (!mouseDownAt.current) {
        return;
      }

      const delta = Date.now() - mouseDownAt.current;

      if (delta > RESET_AFTER) {
        handleReset();
        setIsRunning(false);
        return;
      }

      const progress = normalize(delta, 0, RESET_AFTER, 0, 100);

      setProgress(progress);
    },
    isRunning ? 20 : null
  );

  return (
    <abbr style={{ display: 'inline-block' }}>
      <Tooltip
        direction="n"
        content="Click and hold to reset code to the initial version"
        when={showTooltip}
        triggerAs="span"
      >
        <Wrapper>
          <ActionButton
            title="Reset code"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            onMouseEnter={() => {
              setIsHovering(true);
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setIsHovering(false);
            }}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                handleReset();
              }
            }}
          >
            <SkipBackIcon
              size={16}
              isHovering={isHovering}
              isRunning={isRunning}
            />
          </ActionButton>
          <ProgressWrapper>
            <DotProgressBar
              // Exaggerate the progress so that it hits 100%
              // a little early. Otherwise, the whole thing
              // disappears when the final circle appears.
              value={((progress || 0) / 100) * 1.2}
              dotSize={3}
              numOfDots={5}
              dotColor={accentColor}
            />
          </ProgressWrapper>
        </Wrapper>
      </Tooltip>
    </abbr>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const ProgressWrapper = styled.div`
  position: absolute;
  top: 4px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

export default ResetButton;
